/** Shared checkpoint capture and comparison vocabulary. */

export type VisualRect = { x: number; y: number; width: number; height: number };

/** Structures a run can sit in: the nearest table/grid or list around it. */
export const VISUAL_TEXT_CONTAINERS = ['table', 'list'] as const;
export type VisualTextContainer = (typeof VISUAL_TEXT_CONTAINERS)[number];

/**
 * ARIA landmarks a run can sit in. `<header>`/`<footer>` count as banner/contentinfo only outside
 * article, aside, main, nav and section; `alertdialog` reads as dialog; `<form>` and `<section>`
 * count only with an explicit role (ARIA makes them landmarks only when named).
 */
export const VISUAL_TEXT_LANDMARKS = [
  'navigation',
  'main',
  'banner',
  'contentinfo',
  'complementary',
  'form',
  'dialog',
  'search',
  'region',
] as const;
export type VisualTextLandmark = (typeof VISUAL_TEXT_LANDMARKS)[number];

/**
 * Generic DOM facts about a text run, read with the run. A fact that does not apply is left out:
 * no role, no heading level, no container, no landmark.
 */
export type VisualTextContext = {
  /** Lowercase tag of the element holding the text (`span`, `h1`, `input`); `unknown` for odd names. */
  tag: string;
  /**
   * Explicit or implicit ARIA role of the nearest element that has one (the first token of a `role`
   * attribute, plain lowercase words only), landmarks, `presentation` and `none` skipped.
   */
  role?: string;
  /** 1-6, when that role is a heading. */
  level?: number;
  container?: VisualTextContainer;
  landmark?: VisualTextLandmark;
};

/** One visible text node at capture time, in viewport pixels. */
export type VisualTextRun = { text: string; rect: VisualRect; context: VisualTextContext };

/**
 * Why a visual capture was taken when it was, so a capture of a page that was still changing can be
 * explained.
 *  - `settled`: the page stopped moving and every request it was waiting on had answered.
 *  - `budget`: the settle budget ran out; `blockers` names what was still moving: `network:<METHOD>
 *    <origin+path>` for up to three unanswered requests, `navigation`, or the page-side blockers
 *    such as `mutations`, `toast` or `text:Loading...`.
 *  - `unavailable`: the gate could not run; `blockers` is `hung` (the page evaluate never returned)
 *    or `error:<message>`.
 */
export type VisualCaptureSettle = {
  reason: 'settled' | 'budget' | 'unavailable';
  waitedMs: number;
  blockers: string[];
};

/** How a text run differs between the baseline and the capture. */
export type VisualTextChangeKind = 'changed' | 'added' | 'removed' | 'moved';

/** One side of a text change: the run as it was in the baseline or is in the capture. */
export type VisualTextChangeSide = {
  text: string;
  rect: VisualRect;
  context: VisualTextContext;
};

/** One text difference that falls inside a diff region of a compared screen. */
export type VisualTextChange = {
  kind: VisualTextChangeKind;
  /** The baseline run; null when `added`. */
  before: VisualTextChangeSide | null;
  /** The capture run; null when `removed`. */
  after: VisualTextChangeSide | null;
  /**
   * Index into the same comparison's `regions` of the diff region this change overlaps most. Any
   * step that filters or reorders `regions` must remap it.
   */
  region: number;
};

/**
 * Text differences of one compared screen, aligned from both sides' text runs. Stored with the
 * comparison; nothing is flagged or ignored because of it.
 */
export type VisualTextChanges = {
  /**
   * Ordered by region, then top to bottom, left to right. When there are more than a cap, regions
   * take turns (every region's first change before any region's second), so no region is crowded
   * out by a busier one.
   */
  changes: VisualTextChange[];
  /** How many changes there were before the cap. */
  total: number;
  /**
   * Indexes of diff regions explained by text alone: every change in them is the same run with new
   * text on the same line (no text added, removed or moved, and not a form field's value), and the
   * runs' boxes cover the region.
   */
  textOnlyRegions: number[];
};

/**
 * A region left out of the comparison because the same dynamic text (a date, id, ...) sat there in
 * both the baseline and the capture and only its value changed. `kind` names the dynamic parts.
 */
export type VisualAutoIgnore = { kind: string; rect: VisualRect };

export type VisualExplanation = {
  verdict: 'real' | 'matches_expected' | 'unsure';
  intent: 'intended' | 'likely_bug' | 'unclear';
  text: string;
};

export type VisualRenderProfile = {
  browserVersion: string;
  platform: string;
  viewportWidth: number;
  viewportHeight: number;
  deviceScaleFactor: number;
  locale: string;
  timezone: string;
  colorScheme: 'light' | 'dark' | 'no-preference';
  fontFingerprint: string;
};
